import { defineStore } from 'pinia';

export interface Department {
    id: string;
    name: string;
    appearance: Appearance;
    productList: Product[];
}

export interface Product {
    basic: Basic;
    stock: Stock;
    scale: Scale;
    setItems: SetItems[];
    priceLevels: PriceLevel[];
    condiments: Condiments;
    linkGroups: LinkGroups[];
    appearance: Appearance;
    variation: Variation;
    miscellaneous: Miscellaneous;
}

export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
    displayMode: string;
}

export interface Basic {
    department: string;
    productNumber: string;
    name: string;
    barcode: string;
    taxRate: string;
    serviceCharge: string;
    alternateName: string;
    alternateName2: string;
    displayOrder: number;
    emptyBtn: number;
    singleItemSale: boolean;
    showOnMenu: boolean;
    altDisplayLang: String[];
}

export interface Stock {
    autoStock: boolean;
    returnsCanBeRestocked: boolean;
    stockSource: boolean;
    stockLocation: string;
    stockLowWatermark: number;
    stockHighWatermark: number;
    saleToStockUnitRatio: number;
    currentStockLevel: number;
    saleUnit: string;
    stockUnit: string;
}

export interface Scale {
    scaleItem: boolean;
    weightDecimalPlaces: number;
    scaleMultiplier: number;
    tare: number;
}

export interface SetItems {
    id: string;
    label: string;
    basePrice: number;
    quantity: number;
    allowReduction: boolean;
    linkGroup: LinkGroups;
    presetItem: Product;
}

export interface PriceLevel {
    priceLevel: string;
    price: number;
    HALO: number;
    LALO: number;
}

export interface Condiment {
    id: string;
    name: string;
}

export interface Condiments {
    required: boolean;
    condimentList: Condiment[];
}

export interface LinkGroups {
    id: string;
    name: string;
    appearance: Appearance;
}

export interface VariationItem {
    id: string;
    variation: string;
    productData: Product;
}

export interface Variation {
    required: boolean;
    sourceProduct: boolean;
    variationItem: VariationItem[];
}

export interface Miscellaneous {
    requiredMemo: boolean;
    memo: string;
    ageVerificationRequired: boolean;
    noMembershipDiscountAllowed: boolean;
    nonDiscountable: boolean;
    nonSurchargeable: boolean;
}

export const defaultProduct: Product = {
    appearance: {
        image: '',
        buttonColor: '',
        fontSizes: '',
        displayMode: ''
    },
    basic: {
        department: '',
        productNumber: '',
        name: '',
        barcode: '',
        taxRate: '',
        serviceCharge: '',
        alternateName: '',
        alternateName2: '',
        displayOrder: 0,
        emptyBtn: 0,
        singleItemSale: false,
        showOnMenu: false,
        altDisplayLang: []
    },
    stock: {
        autoStock: false,
        returnsCanBeRestocked: false,
        stockSource: false,
        stockLocation: '',
        stockLowWatermark: 0,
        stockHighWatermark: 0,
        saleToStockUnitRatio: 0,
        currentStockLevel: 0,
        saleUnit: '',
        stockUnit: ''
    },
    scale: {
        scaleItem: false,
        weightDecimalPlaces: 0,
        scaleMultiplier: 0,
        tare: 0
    },
    setItems: [],
    priceLevels: [],
    condiments: {
        required: false,
        condimentList: []
    },
    linkGroups: [],
    variation: {
        required: false,
        sourceProduct: false,
        variationItem: []
    },
    miscellaneous: {
        requiredMemo: false,
        memo: '',
        ageVerificationRequired: false,
        noMembershipDiscountAllowed: false,
        nonDiscountable: false,
        nonSurchargeable: false
    }
};
export const defaultSetItem: SetItems = {
    id: '',
    label: '',
    basePrice: 0,
    quantity: 0,
    allowReduction: false,
    linkGroup: {} as LinkGroups,
    presetItem: {} as Product,
}

export const defaultDepartment: Department = {
    id: '',
    name: '',
    appearance: {
        image: '',
        buttonColor: '',
        fontSizes: '',
        displayMode: ''
    },
    productList: []
};

export const mockDepartments: Department[] = [
    {
        id: '1',
        name: 'Appetizers',
        appearance: {
            image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
            buttonColor: '2', // Amber
            fontSizes: '1.2rem',
            displayMode: 'grid',
        },
        productList: [
            {
                basic: {
                    department: 'Appetizers',
                    productNumber: '101',
                    name: 'Mozzarella Sticks',
                    barcode: '1234567890',
                    taxRate: 'Tax',
                    serviceCharge: 'ser',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 1,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 10,
                    stockHighWatermark: 50,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 30,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 5.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
            {
                basic: {
                    department: 'Appetizers',
                    productNumber: '102',
                    name: 'Chicken Wings',
                    barcode: '9876543210',
                    taxRate: 'Tax',
                    serviceCharge: 'ser',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 2,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 10,
                    stockHighWatermark: 50,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 25,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 7.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
            {
                basic: {
                    department: 'Appetizers',
                    productNumber: '103',
                    name: 'Onion Rings',
                    barcode: '1010101010',
                    taxRate: 'Tax3',
                    serviceCharge: 'ser3',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 3,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 10,
                    stockHighWatermark: 50,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 40,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 4.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
            {
                basic: {
                    department: 'Appetizers',
                    productNumber: '104',
                    name: 'French Fries',
                    barcode: '0101010101',
                    taxRate: 'Tax',
                    serviceCharge: 'ser',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 4,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 10,
                    stockHighWatermark: 50,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 35,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 3.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
            {
                basic: {
                    department: 'Appetizers',
                    productNumber: '105',
                    name: 'Garlic Bread',
                    barcode: '1212121212',
                    taxRate: 'Tax',
                    serviceCharge: 'ser',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 5,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 10,
                    stockHighWatermark: 50,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 20,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 4.50, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
        ],
    },
    {
        id: '2',
        name: 'Main Courses',
        appearance: {
            image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
            buttonColor: '2', // Red
            fontSizes: '1.5rem',
            displayMode: 'grid',
        },
        productList: [
            {
                basic: {
                    department: 'Main Courses',
                    productNumber: '201',
                    name: 'Spaghetti Carbonara',
                    barcode: '1111111111',
                    taxRate: 'Tax1',
                    serviceCharge: 'ser1',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 1,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 5,
                    stockHighWatermark: 20,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 15,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 12.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
            {
                basic: {
                    department: 'Main Courses',
                    productNumber: '202',
                    name: 'Grilled Salmon',
                    barcode: '2222222222',
                    taxRate: 'Tax1',
                    serviceCharge: 'ser1',
                    alternateName: '',
                    alternateName2: '',
                    displayOrder: 2,
                    emptyBtn: 0,
                    singleItemSale: false,
                    showOnMenu: true,
                    altDisplayLang: []
                },
                stock: {
                    autoStock: true,
                    returnsCanBeRestocked: true,
                    stockSource: true,
                    stockLocation: 'Kitchen',
                    stockLowWatermark: 5,
                    stockHighWatermark: 20,
                    saleToStockUnitRatio: 1,
                    currentStockLevel: 10,
                    saleUnit: 'Individually',
                    stockUnit: 'Individually',
                },
                scale: {
                    scaleItem: false,
                    weightDecimalPlaces: 0,
                    scaleMultiplier: 1,
                    tare: 0,
                },
                setItems: [],
                priceLevels: [
                    { priceLevel: 'Regular', price: 18.99, HALO: 0, LALO: 0 },
                ],
                condiments: {
                    required: false,
                    condimentList: [],
                },
                linkGroups: [],
                appearance: {
                    image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
                    buttonColor: '7',
                    fontSizes: 'Medium',
                    displayMode: ''
                },
                variation: {
                    required: false,
                    sourceProduct: false,
                    variationItem: []
                },
                miscellaneous: {
                    requiredMemo: false,
                    memo: '',
                    ageVerificationRequired: false,
                    noMembershipDiscountAllowed: false,
                    nonDiscountable: false,
                    nonSurchargeable: false
                }
            },
        ],
    },
    {
        id: '3',
        name: 'Desserts',
        appearance: {
            image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
            buttonColor: '2', // Pink
            fontSizes: '1.3rem',
            displayMode: 'list',
        },
        productList: [],
    },
    {
        id: '4',
        name: 'Drinks',
        appearance: {
            image: 'https://trueeye.ai/img/Logo%20small.b573b431.png',
            buttonColor: '2', // Cyan
            fontSizes: '1.1rem',
            displayMode: 'list',
        },
        productList: [],
    },
];

export const useProductsStore = defineStore('product', {
    state: () => ({
        departmentList: [] as Department[],
        activeDepartment: { ...defaultDepartment },
        activeProduct: { ...defaultProduct },
        tempActiveProduct: { ...defaultProduct }
    }),
    getters: {
        getDepartmentList: (state) => {
            return state.departmentList;
        },
        getActiveDepartment: (state) => {
            return state.activeDepartment;
        },
        findDepartmentById: (state) => (id: string) => {
            return state.departmentList.find(department => department.id === id);
        },
        getActiveProduct: (state) => {
            return state.activeProduct;
        },
        getActiveProductId: (state) => {
            return state.activeProduct.basic.productNumber;
        },
        findProductById: (state) => (id: string) => {
            return state.activeDepartment.productList.find((product: Product) => product.basic.productNumber === id);
        },
        getActiveProductList: (state) => {
            return state.activeDepartment.productList;
        },
        getTempActiveProduct: (state) => {
            return state.tempActiveProduct;
        },
        getIsActiveProductChanged: (state) => {
            return JSON.stringify(state.activeProduct) !== JSON.stringify(state.tempActiveProduct);
        },
        getAllProducts: (state) => (filter: string, search: string) => {
            let products = state.departmentList.map(department => department.productList).flat();
            if (filter !== 'all') {
                const department = state.departmentList.find(department => department.id === filter);
                products = department ? department.productList : [];
            }
            if (search) {
                products = products.filter(product =>
                    product.basic.productNumber.toLowerCase().includes(search.toLowerCase()) ||
                    product.basic.name.toLowerCase().includes(search.toLowerCase()) ||
                    product.basic.barcode.toLowerCase().includes(search.toLowerCase())
                );
            }
            return products;
        },
        getProductDataById: (state) => (id: string) => {
            let products = state.departmentList.map(department => department.productList).flat();
            return products.find(product => product.basic.productNumber === id);
        }
    },
    actions: {
        initializeDepartments(departments: Department[]) {
            this.departmentList = departments;
        },
        initActiveDepartment(department: Department) {
            this.activeDepartment = department;
        },
        initializeProducts(products: Product[]) {
            this.activeDepartment.productList = products;
        },
        addProduct(product: Product) {
            this.departmentList.forEach(department => {
                if (department.name === product.basic.department) {
                    department.productList.push(product);
                }
            });
        },
        removeProduct(id: string) {
            this.activeDepartment.productList = this.activeDepartment.productList.filter((product: Product) => product.basic.productNumber !== id);
            this.activeProduct = { ...defaultProduct };
            this.tempActiveProduct = { ...defaultProduct };
        },
        updateProduct(updatedProduct: Product) {
            const activeIndex = this.activeDepartment.productList.findIndex((product: Product) => product.basic.productNumber === updatedProduct.basic.productNumber);
            if (activeIndex !== -1) {
                this.activeDepartment.productList[activeIndex] = updatedProduct;
            }

            this.departmentList.forEach(department => {
                const productIndex = department.productList.findIndex((product: Product) => product.basic.productNumber === updatedProduct.basic.productNumber);
                if (productIndex !== -1) {
                    department.productList[productIndex] = updatedProduct;
                }
            });
        },
        setActiveProduct(product: Product) {
            this.activeProduct = product;
        },
        setTempActiveProduct(product: Product) {
            this.tempActiveProduct = product;
        },
        initActiveProduct(product: Product) {
            this.activeProduct = product;
            this.tempActiveProduct = product;
        }
    },
});